import React from 'react';
import { Button } from '@/components/ui/button';
import { LogOut, ChevronDown, Menu } from 'lucide-react';

/**
 * AdminNavbar Component
 *
 * Top navigation bar with logo, desktop menu, and user actions
 * Extracted from AdminDashboard to reduce file size
 */
const AdminNavbar = ({
  activeTab,
  setActiveTab,
  adminName,
  onLogout,
  myPropertyOpen,
  setMyPropertyOpen,
  bookingsOpen,
  setBookingsOpen,
  calendarOpen,
  setCalendarOpen,
  myPropertyRef,
  bookingsRef,
  calendarRef,
  onMenuToggle
}) => {
  const closeAllDropdowns = () => {
    setMyPropertyOpen(false);
    setBookingsOpen(false);
    setCalendarOpen(false);
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    closeAllDropdowns();
  };

  return (
    <header className="border-b bg-white/80 backdrop-blur-md z-50 shadow-sm flex-shrink-0">
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center space-x-6">
          {/* Logo */}
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-8 h-8 bg-blue-600 text-white rounded-lg">
              <span className="text-sm font-bold">A</span>
            </div>
            <h1 className="text-xl font-bold text-gray-900">Admin</h1>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {/* Overview */}
            <Button
              onClick={() => handleTabClick('overview')}
              variant={activeTab === 'overview' ? 'default' : 'ghost'}
              size="sm"
            >
              Overview
            </Button>

            {/* Calendar Dropdown */}
            <div className="relative" ref={calendarRef}>
              <Button
                variant={['calendar', 'car-calendar'].includes(activeTab) ? 'default' : 'ghost'}
                size="sm"
                onClick={() => {
                  setCalendarOpen(!calendarOpen);
                  setMyPropertyOpen(false);
                  setBookingsOpen(false);
                }}
              >
                Calendar
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
              {calendarOpen && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-white border rounded-md shadow-lg z-50">
                  <button
                    onClick={() => {
                      setActiveTab('calendar');
                      setCalendarOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Room Calendar
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab('car-calendar');
                      setCalendarOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Car Calendar
                  </button>
                </div>
              )}
            </div>

            {/* My Property Dropdown */}
            <div className="relative" ref={myPropertyRef}>
              <Button
                variant={['room-types', 'rooms', 'rates', 'room-closures', 'car-types', 'cars', 'car-rates', 'transfers', 'terms'].includes(activeTab) ? 'default' : 'ghost'}
                size="sm"
                onClick={() => {
                  setMyPropertyOpen(!myPropertyOpen);
                  setBookingsOpen(false);
                  setCalendarOpen(false);
                }}
              >
                My Property
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
              {myPropertyOpen && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-white border rounded-md shadow-lg z-50">
                  <button
                    onClick={() => {
                      setActiveTab('room-types');
                      setMyPropertyOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Room Types
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab('rooms');
                      setMyPropertyOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Rooms
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab('rates');
                      setMyPropertyOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Rates
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab('room-closures');
                      setMyPropertyOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Room Closures
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab('car-types');
                      setMyPropertyOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Car Types
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab('cars');
                      setMyPropertyOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Cars
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab('car-rates');
                      setMyPropertyOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Car Rates
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab('transfers');
                      setMyPropertyOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Transfers
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab('terms');
                      setMyPropertyOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Terms & Conditions
                  </button>
                </div>
              )}
            </div>

            {/* Bookings Dropdown */}
            <div className="relative" ref={bookingsRef}>
              <Button
                variant={['bookings', 'customers'].includes(activeTab) ? 'default' : 'ghost'}
                size="sm"
                onClick={() => {
                  setBookingsOpen(!bookingsOpen);
                  setMyPropertyOpen(false);
                  setCalendarOpen(false);
                }}
              >
                Bookings
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
              {bookingsOpen && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-white border rounded-md shadow-lg z-50">
                  <button
                    onClick={() => {
                      setActiveTab('bookings');
                      setBookingsOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    List View
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab('customers');
                      setBookingsOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Customers
                  </button>
                </div>
              )}
            </div>

            {/* Payments */}
            <Button
              onClick={() => handleTabClick('payments')}
              variant={activeTab === 'payments' ? 'default' : 'ghost'}
              size="sm"
            >
              Payments
            </Button>

            {/* Audit */}
            <Button
              onClick={() => handleTabClick('audit')}
              variant={activeTab === 'audit' ? 'default' : 'ghost'}
              size="sm"
            >
              Audit
            </Button>

            {/* Settings */}
            <Button
              onClick={() => handleTabClick('settings')}
              variant={activeTab === 'settings' ? 'default' : 'ghost'}
              size="sm"
            >
              Settings
            </Button>
          </nav>
        </div>

        {/* Right Side Actions */}
        <div className="flex items-center space-x-3">
          {/* Admin Name - Desktop */}
          <span className="hidden md:block text-sm text-gray-700">
            {adminName}
          </span>

          {/* Logout Button - Desktop */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onLogout}
            className="hidden lg:flex text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onMenuToggle}
            className="lg:hidden"
          >
            <Menu className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </header>
  );
};

export default AdminNavbar;
